import { useCallback, useState } from 'react';
import type { UserProfileData } from '@contexts/AuthContextStore';

const STORAGE_KEY_PREFIX = 'aibuild.onboarding.draft.';

/** Per-user localStorage key so multiple users on the same machine don't share drafts. */
export function onboardingDraftKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

/** Discard any partially-filled draft for {@code userId} (called on submit / skip / logout). */
export function clearOnboardingDraft(userId: string): void {
  try {
    localStorage.removeItem(onboardingDraftKey(userId));
  } catch {
    /* localStorage unavailable — nothing to clear. */
  }
}

interface DraftState {
  step: number;
  data: UserProfileData;
}

const EMPTY_DRAFT: DraftState = { step: 0, data: {} };

function readDraft(userId: string): DraftState {
  try {
    const raw = localStorage.getItem(onboardingDraftKey(userId));
    if (!raw) return EMPTY_DRAFT;
    const parsed = JSON.parse(raw) as Partial<DraftState>;
    return {
      step: typeof parsed.step === 'number' ? parsed.step : 0,
      data: parsed.data ?? {},
    };
  } catch {
    return EMPTY_DRAFT;
  }
}

/**
 * Persist partially-filled onboarding answers to {@code localStorage} so the
 * user doesn't lose progress on refresh / accidental tab close. Keyed by user
 * id and cleared on submit / skip / logout.
 *
 * Intentionally does NOT push partial data to the backend — partial answers
 * are never useful for analytics and would just create noise.
 */
function writeDraft(userId: string, state: DraftState): void {
  try {
    localStorage.setItem(onboardingDraftKey(userId), JSON.stringify(state));
  } catch {
    /* localStorage unavailable — silently drop. */
  }
}

export function useOnboardingDraft(userId: string) {
  const [state, setState] = useState<DraftState>(() => readDraft(userId));

  // If the userId changes (e.g. fast user switch in the same tab) refresh
  // the draft from storage so we don't accidentally save user A's answers
  // under user B's key. Following the React docs' "adjust state during
  // rendering" pattern (https://react.dev/learn/you-might-not-need-an-effect)
  // — React discards the in-progress render and re-runs with the fresh state.
  const [trackedUserId, setTrackedUserId] = useState(userId);
  if (trackedUserId !== userId) {
    setTrackedUserId(userId);
    setState(readDraft(userId));
  }

  // Persist on every mutation. Reset() removes the entry rather than
  // overwriting it with the empty default, so a clean draft state is
  // indistinguishable from "never started".
  const setStep = useCallback(
    (step: number) => {
      setState((prev) => {
        const next = { ...prev, step };
        writeDraft(userId, next);
        return next;
      });
    },
    [userId],
  );

  const setData = useCallback(
    (updater: (prev: UserProfileData) => UserProfileData) => {
      setState((prev) => {
        const next = { ...prev, data: updater(prev.data) };
        writeDraft(userId, next);
        return next;
      });
    },
    [userId],
  );

  const reset = useCallback(() => {
    clearOnboardingDraft(userId);
    setState(EMPTY_DRAFT);
  }, [userId]);

  return { step: state.step, data: state.data, setStep, setData, reset };
}
